/**
 * The box you type into, on both surfaces.
 *
 * Files are staged above it rather than inside it: a chip row inside the
 * textarea pushes the caret around as attachments are added and removed, and
 * the one thing this control must never do is move while somebody is writing.
 *
 * Drag-and-drop is handled here too — a person with a spreadsheet open drags it
 * onto the conversation, not onto a paperclip button they have to find first.
 */

import { useRef, useState, type DragEvent } from 'react';
import { ArrowUp, Paperclip, Square, X } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import {
  PromptInput,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
} from '@/components/shadcn/prompt-input';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { ACCEPT, MAX_FILES } from './useAgentChat';
import { ModelPicker } from './ModelPicker';

export function Composer({
  value,
  onChange,
  onSend,
  onStop,
  pending,
  files,
  onAddFiles,
  onRemoveFile,
  autoFocus,
  className,
  model,
  onModelChange,
  thinking,
  onThinkingChange,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onStop: () => void;
  pending: boolean;
  files: File[];
  onAddFiles: (files: FileList | File[] | null) => void;
  onRemoveFile: (index: number) => void;
  autoFocus?: boolean;
  className?: string;
  /** Which tier, or 'auto'. Rendered in the toolbar beside the paperclip. */
  model?: string;
  onModelChange?: (id: string) => void;
  thinking?: boolean;
  onThinkingChange?: (v: boolean) => void;
}) {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [dragging, setDragging] = useState(false);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    onAddFiles(e.dataTransfer.files);
  };

  return (
    <div
      className={cn('relative', className)}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {files.length > 0 ? (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {files.map((f, i) => (
            <span
              key={`${f.name}-${i}`}
              dir="auto"
              className="inline-flex items-center gap-1 rounded-md border bg-background px-1.5 py-0.5 text-[11px]"
            >
              <Paperclip className="size-3 text-muted-foreground" aria-hidden />
              <span className="max-w-40 truncate">{f.name}</span>
              <button
                type="button"
                aria-label={t('agent_remove_file')}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onRemoveFile(i)}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <input
        ref={fileRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          onAddFiles(e.target.files);
          // Cleared so picking the SAME file twice still fires a change event.
          if (fileRef.current) fileRef.current.value = '';
        }}
      />

      <PromptInput
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
        className={cn(dragging && 'ring-2 ring-primary ring-offset-1')}
      >
        <PromptInputTextarea
          ref={inputRef}
          autoFocus={autoFocus}
          // Follows what is being TYPED, not the interface language: an Arabic
          // user pasting an English error should read it left to right.
          dir="auto"
          value={value}
          placeholder={t('agent_placeholder')}
          onChange={(e) => onChange(e.target.value)}
          onSubmitRequest={onSend}
        />

        <PromptInputToolbar>
          <PromptInputTools>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('agent_attach')}
              disabled={pending || files.length >= MAX_FILES}
              onClick={() => fileRef.current?.click()}
            >
              <Paperclip className="size-4" />
            </Button>

            {/* How carefully to work, priced. See ModelPicker. */}
            {model && onModelChange && onThinkingChange ? (
              <ModelPicker
                value={model}
                onChange={onModelChange}
                thinking={Boolean(thinking)}
                onThinkingChange={onThinkingChange}
                disabled={pending}
              />
            ) : null}
          </PromptInputTools>

          <PromptInputSubmit
            status={pending ? 'streaming' : 'idle'}
            disabled={!value.trim() && files.length === 0 && !pending}
            aria-label={pending ? t('agent_stop') : t('agent_send')}
            onClick={
              pending
                ? (e) => {
                    e.preventDefault();
                    onStop();
                  }
                : undefined
            }
          >
            {pending ? <Square className="size-3 fill-current" /> : <ArrowUp className="size-4" />}
          </PromptInputSubmit>
        </PromptInputToolbar>
      </PromptInput>
    </div>
  );
}
